import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface WorklistFilter {
  patientName?: string;
  patientId?: string;
  accessionNumber?: string;
  modality?: string;
  studyDateFrom?: string;
  studyDateTo?: string;
  studyDateSpan?: number;
  referringPhysician?: string;
  studyDescription?: string;
  institution?: string;
  patientSex?: string;
  /** When true, restricts results to studies that have at least one
   *  key image flagged. */
  keyImagesOnly?: boolean;
}

/** Saved filter from the server */
export interface SavedFilter {
  filterId: number;
  name: string;
  description: string;
  patientName: string;
  patientId: string;
  studyDescription: string;
  patientSex: string;
  modality: string;
  accessionNumber: string;
  studyDateSpan: number;
  queryType: string;
  isDefault: boolean;
  institution: string;
  referringPhysician: string;
}

export const DATE_SPAN_OPTIONS = [
  { value: 0, label: 'Any Date' },
  { value: 1, label: 'Today' },
  { value: 2, label: 'Yesterday' },
  { value: 3, label: 'Day Before' },
  { value: 4, label: 'Last Week' },
  { value: 5, label: 'Last 2 Weeks' },
  { value: 6, label: 'Last Month' },
  { value: 7, label: 'Last 3 Months' },
  { value: 8, label: 'Last 6 Months' },
  { value: 9, label: 'Last Year' },
];

export interface ColumnConfig {
  key: string;
  label: string;
  visible: boolean;
  width: number;
}

const DEFAULT_COLUMNS: ColumnConfig[] = [
  { key: 'patientName', label: 'Patient Name', visible: true, width: 200 },
  { key: 'patientId', label: 'Patient ID', visible: true, width: 120 },
  { key: 'patientSex', label: 'Sex', visible: true, width: 40 },
  { key: 'patientAge', label: 'Age', visible: true, width: 50 },
  { key: 'accessionNumber', label: 'Accession #', visible: true, width: 120 },
  { key: 'studyDate', label: 'Study Date', visible: true, width: 110 },
  { key: 'modality', label: 'Modality', visible: true, width: 80 },
  { key: 'studyDescription', label: 'Description', visible: true, width: 200 },
  { key: 'referringPhysician', label: 'Ref. Physician', visible: true, width: 160 },
  { key: 'institution', label: 'Institution', visible: false, width: 150 },
  { key: 'department', label: 'Department', visible: false, width: 120 },
  { key: 'readingPhysician', label: 'Reading Dr', visible: false, width: 150 },
  { key: 'performingPhysician', label: 'Performing Dr', visible: false, width: 150 },
  { key: 'operator', label: 'Operator', visible: false, width: 120 },
  { key: 'numImages', label: 'Images', visible: true, width: 60 },
  { key: 'advStatus', label: 'Status', visible: true, width: 80 },
  { key: 'reportStatus', label: 'Report', visible: true, width: 110 },
  // AI Triage: rendered by WorklistPluginColumn. Stacks a badge per
  // installed triage/utility plugin. Default-off: a vanilla install has
  // no plugins loaded, so showing the column header "AI" above a column
  // of empty cells confuses customers. WorklistPage's visibleCols filter
  // *also* strips this column at runtime when no plugins fill the
  // worklist_row_badge slot, so turning it on manually without plugins
  // installed still produces nothing. Flip to true via Columns dropdown
  // once a plugin is active (the Medtatvaa pilot persisted this as true).
  { key: 'aiTriage', label: 'AI', visible: false, width: 110 },
];

interface WorklistState {
  filters: WorklistFilter;
  columns: ColumnConfig[];
  savedFilters: SavedFilter[];
  setFilters: (filters: WorklistFilter) => void;
  setColumns: (columns: ColumnConfig[]) => void;
  resetColumns: () => void;
  setSavedFilters: (filters: SavedFilter[]) => void;
}

export const useWorklistStore = create<WorklistState>()(
  persist(
    (set) => ({
      filters: {},
      columns: DEFAULT_COLUMNS,
      savedFilters: [],
      setFilters: (filters) => set({ filters }),
      setColumns: (columns) => set({ columns }),
      resetColumns: () => set({ columns: DEFAULT_COLUMNS }),
      setSavedFilters: (savedFilters) => set({ savedFilters }),
    }),
    {
      name: 'edgeweb-worklist',
      version: 3,
      // Migrate existing users' persisted columns: append any columns from
      // DEFAULT_COLUMNS that aren't already in their saved list. Preserves
      // their custom ordering / widths / visibility for columns they already
      // have, while surfacing newly-added ones.
      //
      // v2 -> v3: forces the aiTriage column into existing column lists.
      // Earlier builds persisted a v2 state without aiTriage, so a plain
      // "append new keys" wasn't enough — bump the version so this runs
      // for everyone who upgraded without clearing localStorage.
      migrate: (persisted: any, _version: number) => {
        if (!persisted) return persisted;
        if (Array.isArray(persisted.columns)) {
          const existingKeys = new Set(persisted.columns.map((c: ColumnConfig) => c.key));
          const additions = DEFAULT_COLUMNS.filter((c) => !existingKeys.has(c.key));
          if (additions.length > 0) {
            persisted.columns = [...persisted.columns, ...additions];
          }
        }
        return persisted;
      },
    }
  )
);

/** Session-only store for worklist results (not persisted to localStorage) */
interface WorklistSessionState {
  studies: any[];
  /** Unix ms timestamp of the last successful search that produced `studies`.
   *  Used by WorklistPage on remount to detect that a report-status change
   *  fired while it was unmounted (SPA nav to the report editor eats
   *  those events) and refresh accordingly. 0 = never fetched. */
  studiesFetchedAt: number;
  setStudies: (studies: any[]) => void;
}

export const useWorklistSession = create<WorklistSessionState>((set) => ({
  studies: [],
  studiesFetchedAt: 0,
  setStudies: (studies) => set({ studies, studiesFetchedAt: Date.now() }),
}));

/** Convert a SavedFilter to a WorklistFilter for searching */
export function savedFilterToWorklist(sf: SavedFilter): WorklistFilter {
  return {
    patientName: sf.patientName || undefined,
    patientId: sf.patientId || undefined,
    accessionNumber: sf.accessionNumber || undefined,
    modality: sf.modality || undefined,
    studyDateSpan: sf.studyDateSpan || undefined,
    referringPhysician: sf.referringPhysician || undefined,
    studyDescription: sf.studyDescription || undefined,
    institution: sf.institution || undefined,
    patientSex: sf.patientSex || undefined,
  };
}
